import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { getGenreColors } from '../../utils/genreColors';
import type { GenreGlossaryEntry, SubgenreEntry } from '../../data/genreGlossaryData';
import { toTaxonomySlug } from '../../data/subgenreRegistry';
interface SubgenreTier {
  id: string;
  label: string;
  items: SubgenreEntry[];
}

/**
 * Buckets a genre's subgenres into the two READER-FACING tiers: Core and
 * Crossover. The Microgenre tier has been retired from the public UI — those
 * entries fold into Core, since the Genre -> Subgenre -> Content Tag drill-down
 * now expresses that granularity better than a third tab did.
 *
 * Genres whose entries carry no registry type (e.g. Non-Fiction) collapse to a
 * single "All" tier so their card reads exactly as it did before.
 */
const buildTiers = (subgenres: SubgenreEntry[]): SubgenreTier[] => {
  const hasTypes = subgenres.some(sub => sub.type);
  if (!hasTypes) {
    return [{
      id: 'all',
      label: 'All',
      items: subgenres
    }];
  }
  const tiers: SubgenreTier[] = [{
    id: 'core',
    label: 'Core Subgenres',
    items: subgenres.filter(sub => sub.type === 'core' || sub.type === 'microgenre')
  }, {
    id: 'crossover',
    label: 'Crossover Subgenres',
    items: subgenres.filter(sub => sub.type === 'crossover')
  }].filter(tier => tier.items.length > 0);
  const untyped = subgenres.filter(sub => !sub.type);
  if (untyped.length > 0) {
    tiers.push({
      id: 'related',
      label: 'Related',
      items: untyped
    });
  }
  return tiers;
};
const matches = (sub: SubgenreEntry, query: string) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return sub.name.toLowerCase().includes(q) || sub.definition.toLowerCase().includes(q);
};

/** Wraps the matched span of a label so search hits are easy to spot. */
const Highlight: React.FC<{
  text: string;
  query: string;
}> = ({
  text,
  query
}) => {
  if (!query) return <>{text}</>;
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return <>{text}</>;
  return <>
      {text.slice(0, index)}
      <mark className="bg-yellow-200 dark:bg-yellow-500/30 text-inherit rounded px-0.5" data-id="element-1591" data-mp="199ga0yr2tz9y">
        {text.slice(index, index + query.length)}
      </mark>
      {text.slice(index + query.length)}
    </>;
};
interface GenreGlossaryCardProps {
  genre: GenreGlossaryEntry;
  searchQuery: string;
}
export const GenreGlossaryCard: React.FC<GenreGlossaryCardProps> = ({
  genre,
  searchQuery
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const colors = getGenreColors(genre.colorKey);
  const tiers = useMemo(() => buildTiers(genre.subgenres), [genre.subgenres]);

  // Match counts drive both the tab badges and which tab opens while searching.
  const matchCounts = useMemo(() => tiers.map(tier => tier.items.filter(s => matches(s, searchQuery)).length), [tiers, searchQuery]);
  const firstMatchingTier = matchCounts.findIndex(count => count > 0);
  const [activeIndex, setActiveIndex] = useState(0);

  // While searching, jump to the tier that actually contains hits so the user
  // never has to hunt through tabs to find their result.
  useEffect(() => {
    if (searchQuery && firstMatchingTier >= 0) {
      setActiveIndex(firstMatchingTier);
    }
  }, [searchQuery, firstMatchingTier]);
  const safeIndex = Math.min(activeIndex, Math.max(tiers.length - 1, 0));
  const activeTier = tiers[safeIndex];
  const visibleItems = searchQuery ? activeTier?.items.filter(s => matches(s, searchQuery)) || [] : activeTier?.items || [];
  const showTabs = tiers.length > 1;
  return <div className={`rounded-xl border shadow-sm overflow-hidden ${dk.card}`} data-id="element-1592" data-mp="1jxpq16oghm7o">
      {/* Genre header */}
      <div className="p-6 relative" data-id="element-1593" data-mp="1nz17gox6f14s">
        <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{
        backgroundColor: colors.bg
      }} data-id="element-1594" data-mp="1gujo083yhr90" />
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-3" data-id="element-1595" data-mp="1bvfdne5apnh3">
          <div className="flex items-center gap-3" data-id="element-1596" data-mp="172vy4bn1si9n">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{
            backgroundColor: colors.bg
          }} data-id="element-1597" data-mp="125do0hxanx02" />
            <Link to={`/genres/${genre.slug}`} className={`text-2xl font-bold hover:underline ${dk.textHeading}`} data-id="element-1598" data-mp="y4a8jwj5s2zd">
              {genre.name}
            </Link>
            <span className={`text-sm ${dk.textMuted}`} data-id="element-1599" data-mp="o5ut4qthui5p">
              {genre.subgenres.length} subgenres
            </span>
          </div>
          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap self-start" style={{
          backgroundColor: dk.isDark ? colors.bgDark : colors.bgLight,
          color: dk.isDark ? colors.textDark : colors.text
        }} data-id="element-1600" data-mp="2ijdl7l4r98jm">
            {genre.bookCount.toLocaleString()} books
          </div>
        </div>
        <p className={`text-base leading-relaxed ${dk.textSecondary}`} data-id="element-1601" data-mp="2utlbog62q9cz">
          {genre.definition}
        </p>
      </div>

      {genre.subgenres.length > 0 && activeTier && <div className={`border-t ${dk.borderSubtle}`} data-id="element-1602" data-mp="2zusowuqzk0r1">
          {/* Tier tabs — only one tier is on screen at a time */}
          {showTabs && <div role="tablist" aria-label={`${genre.name} subgenre tiers`} className={`flex flex-wrap gap-1 px-4 pt-4 border-b ${dk.borderSubtle}`} data-id="element-1603" data-mp="27525c66l46nj">
              {tiers.map((tier, index) => {
          const isActive = index === safeIndex;
          const count = searchQuery ? matchCounts[index] : tier.items.length;
          const isEmptyUnderSearch = searchQuery && matchCounts[index] === 0;
          return <button key={tier.id} role="tab" id={`${genre.id}-tab-${tier.id}`} aria-selected={isActive} aria-controls={`${genre.id}-panel-${tier.id}`} onClick={() => setActiveIndex(index)} className={`px-3 py-2 -mb-px text-sm font-medium rounded-t-md border-b-2 transition-colors focus:outline-none focus-visible:ring-2 ${rt.inputFocus} ${isActive ? `border-current ${rt.textPrimary}` : `border-transparent ${isEmptyUnderSearch ? dk.textMuted : dk.textSecondary} hover:${dk.textPrimary}`}`} data-id="element-1604" data-mp="32rdcb9euem7z" data-mpi={index}>
                    {tier.label}
                    <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${dk.badgeNeutral}`} data-id="element-1605" data-mp="21axn7lzkrlyt">
                      {count}
                    </span>
                  </button>;
        })}
            </div>}

          <div role={showTabs ? 'tabpanel' : undefined} id={`${genre.id}-panel-${activeTier.id}`} aria-labelledby={showTabs ? `${genre.id}-tab-${activeTier.id}` : undefined} className="p-4 sm:p-6" data-id="element-1606" data-mp="gmv7eeik8ej6">
            {visibleItems.length === 0 ? <p className={`text-sm ${dk.textMuted}`} data-id="element-1607" data-mp="uji88sdmvoud">
                No {activeTier.label.toLowerCase()} subgenres match “
                {searchQuery}”.
              </p> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1608" data-mp="lkn3zlu5gotp">
                {visibleItems.map(sub => <Link key={sub.id} to={`/genres/${genre.slug}/${toTaxonomySlug(sub.name)}`} className={`block p-4 rounded-lg border ${dk.borderSubtle} ${dk.surfaceMuted} hover:shadow-md transition-shadow`} data-id="element-1609" data-mp="pocpu3jfwsb1">
                    <div className="flex justify-between items-start mb-2 gap-2" data-id="element-1610" data-mp="5stgjacpawv6">
                      <h3 className={`font-semibold flex items-center gap-1 ${dk.textPrimary}`} data-id="element-1611" data-mp="u9vdc5xlp3v">
                        <Highlight text={sub.name} query={searchQuery} data-id="element-1612" data-mp="3o0snuvljsjpu" />
                        <ChevronRightIcon className="h-4 w-4 flex-shrink-0 opacity-60" data-id="element-1613" data-mp="3o2lsk5clotv7" />
                      </h3>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${dk.badgeNeutral}`} data-id="element-1614" data-mp="kjsjo5urot7d">
                        {sub.bookCount.toLocaleString()} books
                      </span>
                    </div>
                    <p className={`text-sm ${dk.textSecondary}`} data-id="element-1615" data-mp="as6bkidq7a8u">
                      {sub.definition}
                    </p>
                    {sub.secondaryGenres && sub.secondaryGenres.length > 0 && <p className={`mt-2 text-xs ${dk.textMuted}`} data-id="element-1616" data-mp="1att2f0n9giat">
                        Also listed under {sub.secondaryGenres.join(', ')}
                      </p>}
                  </Link>)}
              </div>}
          </div>
        </div>}
    </div>;
};
export default GenreGlossaryCard;